import { format } from 'date-fns';
import {
  type Interaction,
  type InteractionType,
} from '../types';
import { type TypeOptions, type CloseButtonProps, toast } from 'react-toastify';
import { getSlideUrl } from '@services/utils.service';
import { getPlatform } from '@lib/platforms';
import { v4 } from 'uuid';
import CustomCloseButton from '../components/CustomCloseButton';
import { resolveInteractionType, resolveLegacySlide } from './legacyInteractions';

export { resolveInteractionType, resolveLegacySlide };

export const getInteractionNameFromType = (type: InteractionType) => {
  const interactionType = resolveInteractionType(type);
  switch (interactionType) {
    case 'pollWithOptions':
      return 'Power Polls';
    case 'freeForm':
      return 'Open-Ended Polls';
    case 'map':
      return 'Magic Maps';
    case 'wordCloud':
      return 'Wonder Words';
    case 'wheel':
      return 'Choice Circle';
    case 'emojisEverywhere':
      return 'Emojis Everywhere';
    case 'talkingTiles':
      return 'Talking Tiles';
    case 'transientThought':
      return 'Transient Thoughts';
    case 'newWheel':
      return 'Winning Wheel';
    case 'linkLibrary':
      return 'Link Library';
    case 'Quick Questions':
      return 'Quick Questions';
    case 'textTrack':
      return 'Text Track';
    case 'PDF Slide':
      return 'PDF Slide';
    case 'ratingPolls':
      return 'Rating Polls';
    case 'fantasticFans':
      return 'Fantastic Fans';
    case 'multipleChoice':
      return 'Multiple Choice';
    case 'quiz':
      return 'Quiz';
    case 'leaderboard':
      return 'Leaderboard';
    case 'credits':
      return 'End Credits';
    default:
      return '';
  }
};

export const getFormattedDate = (date?: string) => {
  if (!date) return '';
  return format(new Date(date), 'MMM dd, EEEE • hh:mm a');
};

export const notify = (message: string | React.ReactNode, type: TypeOptions = 'success', options?: { className?: string; position?: 'top-left' | 'top-right' | 'top-center' | 'bottom-left' | 'bottom-right' | 'bottom-center'; toastId?: string }) => {
  toast(message, {
    position: options?.position || 'top-center',
    theme: 'colored',
    autoClose: 3500,
    type,
    // react-toastify drops a toast whose id is already on screen. Callers pass one
    // where the same event can be observed by two mounted surfaces at once — a
    // session form and the settings list both polling the same platform row.
    toastId: options?.toastId,
    className: `shadow-lg ${options?.className || ''}`.trim(),
    style: {
      maxWidth: '672px',
      ...(type === 'warning' ? { color: '#212121' } : {})
    },
    closeButton: type === 'warning' ? (CustomCloseButton as (props: CloseButtonProps) => React.ReactNode) : undefined
  });
};

export const calculateRemainingChars = (text: string = '', limit: number = 255) => {
  if (typeof text !== 'string') return limit;
  return limit - text.length;
};

// Callers pass a slug in some places and a display name in others; the registry
// resolves either, so this no longer needs a case per spelling.
export const getPlatformImageURL = (platform: string): string =>
  getPlatform(platform)?.logo || '';

export const getDefaultTitle = (type: InteractionType) => {
  switch (resolveInteractionType(type)) {
    case 'newWheel':
      return 'And the winner is ...';
    case 'linkLibrary':
      return 'StreamAlive found these links in the chat...';
    case 'Quick Questions':
      return 'StreamAlive found these questions in the chat...';
    case 'textTrack':
      return 'Text from the chat...';
    case 'fantasticFans':
      return 'Your top commenters are...';
    case 'credits':
      return 'Roll the end credits...';
    case 'ratingPolls':
      return 'What would you rate this session?';
    default:
      return '';
  }
};

export const getCardImage = (question?: Interaction): string => {
  if (!question) return '';
  const imgPath = '/images/ros/template';
  const imgMap: Record<string, string> = {
    pollWithOptions: `${imgPath}/powerpolls.svg`,
    freeForm: `${imgPath}/powerpolls.svg`,
    map: `${imgPath}/map.svg`,
    wordCloud: `${imgPath}/wordcloud.svg`,
    wheel: `${imgPath}/choice.svg`,
    emojisEverywhere: `${imgPath}/emoji-list.svg`,
    talkingTiles: `${imgPath}/talking-tiles.svg`,
    transientThought: `${imgPath}/transient-thoughts.svg`,
    newWheel: `${imgPath}/ww.svg`,
    linkLibrary: `${imgPath}/link.svg`,
    'Quick Questions': `${imgPath}/qq.svg`,
    textTrack: `${imgPath}/text-track.svg`,
    'PDF Slide': getSlideUrl({
      interaction: question,
      isLargePreview: true,
      slide: undefined,
    }),
    ratingPolls: `${imgPath}/rating-polls.svg`,
    fantasticFans: `${imgPath}/fantastic-fans.svg`,
    multipleChoice: `${imgPath}/multiple-chouce.svg`,
    quiz: `${imgPath}/quiz.svg`,
  };

  const type = resolveInteractionType(question.type) as keyof typeof imgMap;
  return imgMap[type] || '';
};

export const getDefaultOptions = (interaction_type: InteractionType) => {
  switch (interaction_type) {
    case 'pollWithOptions':
      return [
        { id: v4(), body: 'Option 1' },
        { id: v4(), body: 'Option 2' },
      ];

    case 'wheel':
      return [
        { id: v4(), body: 'Option 1' },
        { id: v4(), body: 'Option 2' },
      ];
    case 'newWheel':
      return [
        { id: v4(), body: 'Liam' },
        { id: v4(), body: 'Olivia' },
        { id: v4(), body: 'Emma' },
      ];
    case 'ratingPolls':
      return [
        { id: v4(), body: '1', option_number: 1 },
        { id: v4(), body: '2', option_number: 2 },
        { id: v4(), body: '3', option_number: 3 },
        { id: v4(), body: '4', option_number: 4 },
        { id: v4(), body: '5', option_number: 5 },
      ];
    case 'multipleChoice':
      return [
        { id: v4(), body: 'Option 1', checked: false, },
        { id: v4(), body: 'Option 2', checked: false, },
      ];
    case 'quiz':
      return [
        { id: v4(), body: 'Option 1', checked: true, },
        { id: v4(), body: 'Option 2', checked: false, },
        { id: v4(), body: 'Option 3', checked: false, },
        { id: v4(), body: 'Option 4', checked: false, },
      ];
    default:
      return [];
  }
};
